import { TFunction } from 'react-i18next';

import {
    buildMealTypeLabel,
    buildMealTypeTooltip,
} from 'component/Offer/Service/__hooks/useMealTypeLabel';

import { TooltipProvider } from 'types/Search/popularFilter';

import { MealType } from 'proto/Offer/MealType';
import { FilterMeta } from 'proto/Search/FilterMeta';

const allInclusiveTypes = new Set([
    MealType.AllInclusive,
    MealType.AllInclusivePlus,
]);

const fullboardPlusTypes = new Set([
    MealType.FullBoardPlus,
    ...allInclusiveTypes,
]);

const fullboardTypes = new Set([MealType.FullBoard, ...fullboardPlusTypes]);

const halfboardPlusTypes = new Set([MealType.HalfBoardPlus, ...fullboardTypes]);

const halfboardTypes = new Set([MealType.HalfBoard, ...halfboardPlusTypes]);

const breakfastTypes = new Set([MealType.Breakfast, ...halfboardTypes]);

const mealTypeMinLogicValueMap: Partial<Record<MealType, Set<MealType>>> = {
    [MealType.AllInclusivePlus]: new Set([MealType.AllInclusivePlus]),
    [MealType.AllInclusive]: allInclusiveTypes,
    [MealType.FullBoardPlus]: fullboardPlusTypes,
    [MealType.FullBoard]: fullboardTypes,
    [MealType.HalfBoardPlus]: halfboardPlusTypes,
    [MealType.HalfBoard]: halfboardTypes,
    [MealType.Breakfast]: breakfastTypes,
    [MealType.None]: new Set([MealType.None]),
};

const minLogicTypes = new Set([
    MealType.FullBoardPlus,
    MealType.FullBoard,
    MealType.HalfBoardPlus,
    MealType.HalfBoard,
    MealType.Breakfast,
]);

const tooltipValues = new Set([
    MealType.AllInclusive,
    MealType.FullBoard,
    MealType.HalfBoard,
    MealType.Breakfast,
]);

export const getMealTypeMinLogicValue = (mealType: MealType): Set<MealType> =>
    mealTypeMinLogicValueMap[mealType] ?? new Set();

export const buildMealTypeFilterLabel = (
    value: MealType,
    t: TFunction,
    meta: Partial<Record<MealType, FilterMeta>> | undefined
) => {
    const label = buildMealTypeLabel(value, t);
    return minLogicTypes.has(value) ||
        (value === MealType.AllInclusive && meta?.[MealType.AllInclusivePlus])
        ? `${t('TRANS-min-abbreviation')} ${label}`
        : label;
};

export const buildMealTypeFilterTooltip: TooltipProvider<MealType> = (
    value,
    t
) => (tooltipValues.has(value) ? buildMealTypeTooltip(value, t) : undefined);

export const buildMealTypeMinLogicMetaKey = (mealType: MealType) =>
    [...getMealTypeMinLogicValue(mealType)].join(',');

export const isMealTypeSelected = (
    mealType: MealType,
    currentState: Set<MealType>
) => currentState.has(mealType) && [...currentState][0] === mealType;
